import { useState, useEffect } from 'react'
import { ID } from "appwrite"
import { client, storage, functions, account, databases as db } from '../appwrite/config'
import { getWidgetData, updateWidgetData } from '../appwrite/methods'
import { useAtom } from 'jotai'
import {
    UserSession,
    TeamsList,
    CurrentTeam,
    AdminUser,
    sanitize,
    kebabToTitle,
    camelToTitle
  } from '../utils'
import { isLoading } from './FileUpload';
import {
    Fab,
    Snackbar
} from '@mui/material';


/**
 * @RunFunction
 *
 * @param {String} name Required. ID of the function to run.
 * @param {String} button Optional. Text for the button. If unspecified, defaults to "Start Widget".
 * @param {String} path Optional. Path passed to the function to run. If unspecified, defaults to '/proc'. This is useful if the same function has more than one possible action.
 * @param {Boolean} async Optional. Whether to run the function asynchronously (in the background). Default is true.
 * 
 * @returns {JSX.Element} Rendered RunFunction component.
*/
/**
 * @TODO
 * - Remove files from connected FileUploader on job process (maybe passed as a prop?)
 */

export default function RunFunction({ name, button, path, async }) {
    const [error, setError] = useState(null)
    const [running, setRunning] = useState(false)
    const [loading, setLoading] = useAtom(isLoading)
    const [outputId, setOutputId] = useState(null)

    const [userSession, setUserSession] = useAtom(UserSession)
    const [teamsList, setTeamsList] = useAtom(TeamsList)
    const [currentTeam, setCurrentTeam] = useAtom(CurrentTeam)

    // const [jobStarted, setJobStarted] = useState(null)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (running) setOpen(true);
    }, [running]);

    // FIXME: update function IDs to use their real name, so this unecessary logic can be removed
    const functionList = {
        test1: '673216030037711ba43a',
        'bid-scanner': '67b78adf001ffc107cf7',
        'schedule-extractor': '679a8748001581c72c25',
        'wall-height-calculator': '67b3a49f0022218dd8e3'
    }
    const functionId = functionList[name] || name

    const handleButtonClick = async () => {
        setRunning(true);
        const newOutputId = ID.unique();
        setOutputId(newOutputId);

        try {
            const fileList = await storage.listFiles(`${userSession.$id}_input`);
            if (fileList.files.length === 0) {
                throw new Error('No files present');
            }
            const fileIds = fileList.files.map(file => file.$id);

            const data = await getWidgetData(
                sanitize(teamsList[currentTeam]),
                userSession.$id,
                kebabToTitle(name)
            );

            const params = Object.entries(data).reduce((acc, [key, value]) => {
                if (!key.startsWith('$') && value !== null) acc[key] = value;
                return acc;
            }, {});

            await functions.createExecution(
                functionId,
                JSON.stringify({
                    jwt: await account.createJWT(),
                    fileIds,
                    settings: params,
                    userData: userSession,
                    outputId: newOutputId
                }),
                async ?? true,
                path || '/proc',
                'GET'
            );

            setRunning(false);
            setOpen(false);

        } catch (error) {
            console.error('Error executing function:', error);
            setError(error);
            setOpen(true);
            setRunning(false);
        }
    }

    return (<>
        <Fab
            variant="extended"
            size="large"
            color="primary"
            loading={running.toString()}
            onClick={handleButtonClick}
            disabled={running || loading}>
            {running ? 'Processing...' : loading ? `Uploading ${loading} file${loading === 1 ? '' : 's'}...` : button || 'Start Widget'}
        </Fab>
        <Snackbar
            open={open}
            onClose={() => setOpen(false)}
            autoHideDuration={10000}
            message={error ? error.message : "Starting widget (this may take a few seconds)..."}
            key="snackbar"
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
    </>)
}